import { z } from "zod";
import { discordSnowflakeSchema, utcTimestampSchema, } from "./guild-settings.js";
import { MAX_ASSISTANT_MEMBER_IDS, MAX_ASSISTANT_ROLE_IDS, } from "./guild-selectors.js";
import { normalizedSnowflakeListSchema, unicodeCodePointLength, utf8ByteLength, } from "./platform-common.js";
export const MAX_GUILD_INSTRUCTION_CODE_POINTS = 2_000;
export const MAX_GUILD_INSTRUCTION_UTF8_BYTES = 8_192;
export const MAX_GUILD_PLAYER_ALIAS_ENTRIES = 500;
export const MAX_GUILD_PLAYER_ALIAS_UTF8_BYTES = 32_768;
export const MAX_GUILD_PLAYER_ALIAS_COMPONENT_CODE_POINTS = 64;
export const MAX_GUILD_CHARACTER_LINKS = 500;
export const MAX_GUILD_CHARACTER_LINKS_PER_USER = 8;
export const MAX_GUILD_CHARACTER_DISPLAY_CODE_POINTS = 64;
export const MAX_GUILD_CHARACTER_DISPLAY_UTF8_BYTES = 256;
export const MAX_GUILD_CHARACTER_KEY_CODE_POINTS = 128;
export const MAX_GUILD_CHARACTER_KEY_UTF8_BYTES = 512;
export const guildResponseToneSchema = z.enum([
    "playful",
    "neutral",
    "professional",
]);
export const guildToneSchema = z
    .object({
    guildId: discordSnowflakeSchema,
    tone: guildResponseToneSchema.nullable(),
    toneRevision: z.int().positive(),
    updatedAt: utcTimestampSchema,
})
    .strict();
export const guildToneUpdateSchema = z
    .object({
    tone: guildResponseToneSchema.nullable(),
    expectedToneRevision: z.int().positive(),
})
    .strict();
export const guildCharacterRegionSchema = z
    .string()
    .transform((value) => value.toLocaleLowerCase("en-US"))
    .pipe(z.enum(["us", "eu", "kr", "tw", "cn"]));
const guildCharacterDisplaySchema = (field) => z.string().transform((value, context) => {
    const display = value.trim().normalize("NFC");
    if (unicodeCodePointLength(display) < 1 ||
        unicodeCodePointLength(display) > MAX_GUILD_CHARACTER_DISPLAY_CODE_POINTS) {
        context.addIssue({
            code: "custom",
            message: `${field} must contain between 1 and ${MAX_GUILD_CHARACTER_DISPLAY_CODE_POINTS} Unicode code points`,
        });
        return z.NEVER;
    }
    if (utf8ByteLength(display) > MAX_GUILD_CHARACTER_DISPLAY_UTF8_BYTES) {
        context.addIssue({
            code: "custom",
            message: `${field} must contain at most ${MAX_GUILD_CHARACTER_DISPLAY_UTF8_BYTES} UTF-8 bytes`,
        });
        return z.NEVER;
    }
    return display;
});
export const guildCharacterLinkSchema = z
    .object({
    discordUserId: discordSnowflakeSchema,
    region: guildCharacterRegionSchema,
    character: guildCharacterDisplaySchema("Character"),
    realm: guildCharacterDisplaySchema("Realm"),
    primary: z.boolean(),
})
    .strict();
export function normalizeGuildCharacterLink(unsafeValue) {
    const value = guildCharacterLinkSchema.parse(unsafeValue);
    const normalizedCharacter = value.character.toLocaleLowerCase("en-US");
    const normalizedRealm = value.realm
        .normalize("NFKD")
        .replace(/\p{M}/gu, "")
        .toLocaleLowerCase("en-US")
        .replace(/[^\p{L}\p{N}]+/gu, "");
    assertCharacterKey("Character", normalizedCharacter);
    assertCharacterKey("Realm", normalizedRealm);
    return { ...value, normalizedCharacter, normalizedRealm };
}
function assertCharacterKey(field, value) {
    if (unicodeCodePointLength(value) < 1 ||
        unicodeCodePointLength(value) > MAX_GUILD_CHARACTER_KEY_CODE_POINTS ||
        utf8ByteLength(value) > MAX_GUILD_CHARACTER_KEY_UTF8_BYTES) {
        throw new RangeError(`Invalid normalized ${field.toLocaleLowerCase()}`);
    }
}
const guildCharacterLinkListSchema = z
    .array(guildCharacterLinkSchema)
    .max(MAX_GUILD_CHARACTER_LINKS)
    .superRefine((links, context) => {
    const identities = new Set();
    const userCounts = new Map();
    const primaryUsers = new Set();
    for (const [index, link] of links.entries()) {
        let normalized;
        try {
            normalized = normalizeGuildCharacterLink(link);
        }
        catch {
            context.addIssue({
                code: "custom",
                message: "Character link has an invalid normalized identity",
                path: [index],
            });
            continue;
        }
        const identity = `${normalized.region}\u0000${normalized.normalizedRealm}\u0000${normalized.normalizedCharacter}`;
        if (identities.has(identity)) {
            context.addIssue({
                code: "custom",
                message: "A character may be linked only once within a guild",
                path: [index],
            });
        }
        identities.add(identity);
        const count = (userCounts.get(link.discordUserId) ?? 0) + 1;
        userCounts.set(link.discordUserId, count);
        if (count > MAX_GUILD_CHARACTER_LINKS_PER_USER) {
            context.addIssue({
                code: "custom",
                message: `A Discord user may have at most ${MAX_GUILD_CHARACTER_LINKS_PER_USER} character links`,
                path: [index, "discordUserId"],
            });
        }
        if (link.primary) {
            if (primaryUsers.has(link.discordUserId)) {
                context.addIssue({
                    code: "custom",
                    message: "A Discord user may have only one primary character",
                    path: [index, "primary"],
                });
            }
            primaryUsers.add(link.discordUserId);
        }
    }
})
    .transform((links) => [...links].sort(compareCharacterLinks));
function compareCharacterLinks(left, right) {
    const userOrder = left.discordUserId.length - right.discordUserId.length ||
        left.discordUserId.localeCompare(right.discordUserId);
    if (userOrder !== 0)
        return userOrder;
    const leftIdentity = normalizeGuildCharacterLink(left);
    const rightIdentity = normalizeGuildCharacterLink(right);
    return (leftIdentity.region.localeCompare(rightIdentity.region) ||
        leftIdentity.normalizedRealm.localeCompare(rightIdentity.normalizedRealm) ||
        leftIdentity.normalizedCharacter.localeCompare(rightIdentity.normalizedCharacter) ||
        Number(right.primary) - Number(left.primary));
}
export const guildCharacterLinksSchema = z
    .object({
    guildId: discordSnowflakeSchema,
    links: guildCharacterLinkListSchema,
    characterLinksRevision: z.int().positive(),
    updatedAt: utcTimestampSchema,
})
    .strict();
export const guildCharacterLinksUpdateSchema = z
    .object({
    links: guildCharacterLinkListSchema,
    expectedCharacterLinksRevision: z.int().positive(),
})
    .strict();
export function normalizeGuildInstructions(value) {
    const normalized = value
        .replace(/\r\n?/g, "\n")
        .replace(/^[ \t\n\r\f\v]+|[ \t\n\r\f\v]+$/g, "")
        .normalize("NFC");
    return normalized === "" ? null : normalized;
}
export const guildInstructionContentSchema = z
    .union([z.string(), z.null()])
    .transform((value) => normalizeGuildInstructions(value ?? ""))
    .superRefine((value, context) => {
    if (value === null)
        return;
    if (unicodeCodePointLength(value) > MAX_GUILD_INSTRUCTION_CODE_POINTS) {
        context.addIssue({
            code: "custom",
            message: `Instructions must contain at most ${MAX_GUILD_INSTRUCTION_CODE_POINTS} Unicode code points`,
        });
    }
    if (utf8ByteLength(value) > MAX_GUILD_INSTRUCTION_UTF8_BYTES) {
        context.addIssue({
            code: "custom",
            message: `Instructions must contain at most ${MAX_GUILD_INSTRUCTION_UTF8_BYTES} UTF-8 bytes`,
        });
    }
});
export const guildInstructionsSchema = z
    .object({
    guildId: discordSnowflakeSchema,
    content: guildInstructionContentSchema,
    revision: z.int().positive(),
    updatedAt: utcTimestampSchema,
})
    .strict();
export const guildInstructionsReplacementSchema = z
    .object({
    content: guildInstructionContentSchema,
    expectedRevision: z.int().positive(),
})
    .strict();
export function normalizeGuildPlayerAliases(value) {
    const normalized = value.replace(/\r\n?/g, "\n").normalize("NFC").trim();
    if (normalized === "")
        return null;
    return normalized
        .split(/[;\n]+/)
        .map((entry) => entry.trim())
        .filter(Boolean)
        .join(";\n");
}
function playerAliasEntries(value) {
    return value.split(";\n");
}
function parsePlayerAliasEntry(entry) {
    const colon = entry.indexOf(":");
    if (colon < 1 || colon !== entry.lastIndexOf(":"))
        return undefined;
    const characterRealm = entry.slice(0, colon).trim();
    const nickname = entry.slice(colon + 1).trim();
    const hyphen = characterRealm.indexOf("-");
    if (hyphen < 1)
        return undefined;
    const character = characterRealm.slice(0, hyphen).trim();
    const realm = characterRealm.slice(hyphen + 1).trim();
    if (!character || !realm || !nickname)
        return undefined;
    return { character, nickname, realm };
}
export function playerAliasIdentityKey(character, realm) {
    return `${character.normalize("NFC").toLocaleLowerCase("en-US")}-${realm
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLocaleLowerCase("en-US")
        .replace(/[^\p{L}\p{N}]+/gu, "")}`;
}
export const guildPlayerAliasContentSchema = z
    .union([z.string(), z.null()])
    .transform((value) => normalizeGuildPlayerAliases(value ?? ""))
    .superRefine((value, context) => {
    if (value === null)
        return;
    if (utf8ByteLength(value) > MAX_GUILD_PLAYER_ALIAS_UTF8_BYTES) {
        context.addIssue({
            code: "custom",
            message: `Player aliases must contain at most ${MAX_GUILD_PLAYER_ALIAS_UTF8_BYTES} UTF-8 bytes`,
        });
    }
    const entries = playerAliasEntries(value);
    if (entries.length > MAX_GUILD_PLAYER_ALIAS_ENTRIES) {
        context.addIssue({
            code: "custom",
            message: `Player aliases must contain at most ${MAX_GUILD_PLAYER_ALIAS_ENTRIES} entries`,
        });
    }
    const identities = new Set();
    for (const entry of entries) {
        const parsed = parsePlayerAliasEntry(entry);
        if (!parsed) {
            context.addIssue({
                code: "custom",
                message: `Invalid player alias entry: ${entry}`,
            });
            continue;
        }
        if ([parsed.character, parsed.realm, parsed.nickname].some((part) => unicodeCodePointLength(part) >
            MAX_GUILD_PLAYER_ALIAS_COMPONENT_CODE_POINTS)) {
            context.addIssue({
                code: "custom",
                message: `Player alias components must contain at most ${MAX_GUILD_PLAYER_ALIAS_COMPONENT_CODE_POINTS} Unicode code points`,
            });
        }
        const identity = playerAliasIdentityKey(parsed.character, parsed.realm);
        if (identities.has(identity)) {
            context.addIssue({
                code: "custom",
                message: `Duplicate player alias identity: ${parsed.character}-${parsed.realm}`,
            });
        }
        identities.add(identity);
    }
});
export function parseGuildPlayerAliases(value) {
    const content = guildPlayerAliasContentSchema.parse(value);
    if (content === null)
        return new Map();
    return new Map(playerAliasEntries(content).map((entry) => {
        const parsed = parsePlayerAliasEntry(entry);
        return [
            playerAliasIdentityKey(parsed.character, parsed.realm),
            parsed.nickname,
        ];
    }));
}
export const guildPlayerAliasesSchema = z
    .object({
    guildId: discordSnowflakeSchema,
    content: guildPlayerAliasContentSchema,
    revision: z.int().positive(),
    updatedAt: utcTimestampSchema,
})
    .strict();
export const guildPlayerAliasesReplacementSchema = z
    .object({
    content: guildPlayerAliasContentSchema,
    expectedRevision: z.int().positive(),
})
    .strict();
const assistantRoleIdsSchema = normalizedSnowflakeListSchema(MAX_ASSISTANT_ROLE_IDS, "Assistant role IDs");
const assistantMemberIdsSchema = normalizedSnowflakeListSchema(MAX_ASSISTANT_MEMBER_IDS, "Assistant member IDs");
export const guildAssistantAccessSchema = z
    .object({
    guildId: discordSnowflakeSchema,
    allowedRoleIds: assistantRoleIdsSchema,
    allowedMemberIds: assistantMemberIdsSchema,
    accessRevision: z.int().positive(),
    updatedAt: utcTimestampSchema,
})
    .strict();
export const guildAssistantAccessReplacementSchema = z
    .object({
    allowedRoleIds: assistantRoleIdsSchema,
    allowedMemberIds: assistantMemberIdsSchema,
    expectedAccessRevision: z.int().positive(),
})
    .strict();
//# sourceMappingURL=guild-customization.js.map